// FeedbackList.tsx
import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Avatar, 
  Card, 
  CardContent, 
  CardActions,
  Collapse,
  IconButton,
  Badge,
  Divider,
  Paper,
  Tooltip,
  Fade,
  Skeleton,
  Pagination
} from '@mui/material';
import { 
  Comment as CommentIcon, 
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  SubdirectoryArrowRight as ReplyIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import axiosInstance, {publicInstance} from '../axiosInstance';
import { useAtom } from 'jotai';
import { feedbackCountAtom } from '../../helpers/atoms';
import { styled } from '@mui/material/styles';
import ReplyForm from './ReplyForm';
import { useUser } from '../../Components/UserContext';

interface FeedbackListProps {
  refreshFeedback: boolean;
  variant?: 'current' | 'deleted' | 'user';
  userId?: number | null;
  updateDeletedFeedbackCount?: (count: number) => void;
  updateCurrentFeedbackCount?: (count: number) => void;
}

interface FeedbackItem {
  id: number;
  user_id: number;
  username: string;
  comment: string;
  time_stamp: string;
  replies_count: number;
  deletion_time?: string; // Optional for deleted feedback
}

interface ThreadResponse {
  id: number;
  parent_id: null | number;
  user_id: number;
  username: string;
  comment: string;
  time_stamp: string;
  deletion_time?: string;
  level: number;
  path: number[];
  replies: ThreadResponse[];
  profile_image_path?: string;
}

interface User {
  username: string;
  profile_image_path?: string;
}

interface PaginatedResponse {
  total: number;
  threads: FeedbackItem[];
  skip: number;
  limit: number;
}

interface DeleteResponse {
  status: string;
  message: string;
  deleted_id: number;
  was_main_thread: boolean;
}

const RenderSkeleton = ({ depth = 0 }) => (
  <Box sx={{ ml: Math.min(depth * 3, 9), mt: 2 }}>
    <Card sx={{ bgcolor: 'background.secondary' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton width={120} height={24} />
            <Skeleton width={160} height={20} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
      </CardContent>
    </Card>
  </Box>
);

const FeedbackList: React.FC<FeedbackListProps> = ({ 
  refreshFeedback, 
  variant = 'current',
  userId,
  updateDeletedFeedbackCount,
  updateCurrentFeedbackCount
}) => {
  const { user } = useUser();
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([]);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [, setFeedbackCount] = useAtom(feedbackCountAtom);
  const [expandedThreads, setExpandedThreads] = useState<{ [key: number]: boolean }>({});
  const [threadResponses, setThreadResponses] = useState<{ [key: number]: ThreadResponse }>({});
  const [loadingThreads, setLoadingThreads] = useState<{ [key: number]: boolean }>({});
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const isDeleted = variant === 'deleted';
  const isAdmin = localStorage.getItem('role') === 'admin';

  const handleDelete = async (id: number, isMainThread: boolean = true) => {
    try {
      const endpoint = isDeleted ? `/feedback/deleted/${id}` : `/feedback/${id}`;
      const response = await axiosInstance.delete(endpoint);
      const data = response.data as DeleteResponse;
      
      if (data.status === 'success') {
        if (isMainThread) {
          setFeedbackList(prev => prev.filter(item => item.id !== id));
          setTotalFeedbacks(prev => prev - 1);
          if (isDeleted && updateDeletedFeedbackCount) {
            updateDeletedFeedbackCount(totalFeedbacks - 1);
          } else {
            setFeedbackCount(prev => prev - 1);
          }
        } else {
          const parentId = Object.keys(threadResponses).find(key => 
            threadResponses[Number(key)].replies?.some(reply => reply.id === id)
          );
          
          if (parentId) {
            handleReplySuccess(Number(parentId));
          }
        }
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleReplySuccess = async (feedbackId: number) => {
    try {
      setLoadingThreads(prev => ({ ...prev, [feedbackId]: true }));
      let endpoint;
      
      if (isDeleted) {
        endpoint = `/feedback/historical/${feedbackId}/thread`;
      } else {
        endpoint = `/feedback/${feedbackId}/thread_2`;
      }
      
      const response = await axiosInstance.get(endpoint);
      const threadData = response.data;
      setThreadResponses(prev => ({ 
        ...prev, 
        [feedbackId]: threadData && typeof threadData === 'object' ? threadData : { replies: [] }
      }));

      // Zaktualizuj licznik odpowiedzi w feedbackList używając rzeczywistej liczby odpowiedzi
      setFeedbackList(prev => prev.map(item => {
        if (item.id === feedbackId) {
          const actualRepliesCount = threadData?.replies?.length || 0;
          return {
            ...item,
            replies_count: actualRepliesCount
          };
        }
        return item;
      }));

      // Automatycznie rozwiń wątek po dodaniu pierwszej odpowiedzi
      setExpandedThreads(prev => ({
        ...prev,
        [feedbackId]: true
      }));
    } catch (error) {
      console.error('Error refreshing thread:', error);
    } finally {
      setLoadingThreads(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleExpandThread = (feedbackId: number) => {
    setExpandedThreads(prev => {
      const newState = { ...prev, [feedbackId]: !prev[feedbackId] };
      if (newState[feedbackId] && !threadResponses[feedbackId]) {
        // Fetch thread responses if expanding and not already loaded
        handleReplySuccess(feedbackId);
      }
      return newState;
    });
  };

  const RenderReply = ({ 
    reply, 
    depth = 0,
    onReplySuccess,
    users
  }: { 
    reply: ThreadResponse; 
    depth?: number;
    onReplySuccess: () => void;
    users: User[];
  }) => {
    const user = users.find(u => u.username === reply.username);

    return (
      <Box sx={{ 
        ml: Math.min(depth * 3, 9),
        mt: 2,
        position: 'relative',
        '&::before': depth > 0 ? {
          content: '""',
          position: 'absolute',
          left: '-16px',
          top: '20px',
          width: '16px',
          height: '2px',
          bgcolor: 'primary.main',
          opacity: 0.3
        } : {}
      }}>
        <Card sx={{ bgcolor: 'background.secondary', position: 'relative' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {depth > 0 && <ReplyIcon sx={{ mr: 1, opacity: 0.7, color: 'primary.main' }} />}
              <Avatar 
                src={user?.profile_image_path}
                alt={reply.username} 
                sx={{ 
                  width: 32, 
                  height: 32,
                  mr: 1,
                  border: 1,
                  borderColor: 'primary.main' 
                }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {reply.username} 
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                  {new Date(reply.time_stamp).toLocaleString()}
                </Typography>
                {isDeleted && reply.deletion_time && (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                    <DeleteIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                    Deleted: {new Date(reply.deletion_time).toLocaleString()}
                  </Typography>
                )}
              </Box>
              {!isDeleted && isAdmin && variant !== 'user' && (
                <Tooltip title="Delete reply">
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(reply.id, false);
                    }}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                bgcolor: 'background.paper',
                p: 2,
                borderRadius: 1,
                color: 'text.primary',
                boxShadow: 'inset 0 0 0 1px rgba(144, 202, 249, 0.08)'
              }}
            >
              {reply.comment}
            </Typography>
            {!isDeleted && (
              <ReplyForm 
                feedbackId={reply.id}
                onReplySuccess={onReplySuccess}
              />
            )}
          </CardContent>
        </Card>
        {reply.replies?.map((nestedReply) => (
          <RenderReply 
            key={nestedReply.id} 
            reply={nestedReply} 
            depth={depth + 1}
            onReplySuccess={onReplySuccess}
            users={users}
          />
        ))}
      </Box>
    );
  };

  useEffect(() => {
    const fetchFeedbacks = async () => {
      const skip = (page - 1) * limit;
      let endpoint;
      
      if (variant === 'user') {
        endpoint = `/feedback/user-threads/${user?.user_id}?skip=${skip}&limit=${limit}`;
      } else if (isDeleted) {
        endpoint = `/feedback/historical/main-threads?skip=${skip}&limit=${limit}`;
      } else {
        endpoint = isAdmin ? 
          `/feedback/main-threads?skip=${skip}&limit=${limit}` : 
          `/feedback/non-admin-threads?skip=${skip}&limit=${limit}`;
      }

      try {
        const response = await axiosInstance.get(endpoint);
        const paginatedData = response.data as PaginatedResponse;
        setFeedbackList(paginatedData.threads || []);
        setTotalFeedbacks(paginatedData.total);
        setTotalPages(Math.ceil(paginatedData.total / limit));
        if (isDeleted && updateDeletedFeedbackCount) {
          updateDeletedFeedbackCount(paginatedData.total);
        } else if (!isDeleted && updateCurrentFeedbackCount) {
          updateCurrentFeedbackCount(paginatedData.total);
          setFeedbackCount(paginatedData.total);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const fetchUsers = () => {
      axiosInstance.get('/auth/users')
        .then(response => {
          setUsers(response.data);
        })
        .catch(error => console.error('Error fetching users:', error));
    };

    if (variant !== 'user' || user) {
      fetchFeedbacks();
      fetchUsers();
    }
  }, [setFeedbackCount, refreshFeedback, page, limit, isAdmin, isDeleted, updateDeletedFeedbackCount, updateCurrentFeedbackCount, variant, user]);

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', margin: 'auto', p: 3, pb: 8 }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 700,
          mb: 4,
          borderBottom: '3px solid',
          borderColor: 'primary.main',
          pb: 1,
          display: 'inline-block',
          color: 'text.primary'
        }}
      >
        {isDeleted ? 'Deleted Feedback History' : 
         variant === 'user' ? 'My Feedback' : 
         'Community Feedback'} ({totalFeedbacks})
      </Typography>

      {feedbackList.map((feedback) => {
        const user = users.find(u => u.username === feedback.username);
        const hasReplies = feedback.replies_count > 0;
        const isExpanded = expandedThreads[feedback.id] || false;

        return (
          <Paper 
            key={feedback.id} 
            elevation={2}
            sx={{ 
              mb: 3,
              bgcolor: 'background.paper',
              ...(hasReplies ? { borderLeft: '4px solid', borderLeftColor: 'primary.main' } : {}),
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Card sx={{ bgcolor: 'transparent', boxShadow: 'none',  }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar 
                    src={user?.profile_image_path} 
                    alt={feedback.username}
                    sx={{ 
                      width: 48, 
                      height: 48,
                      border: 2,
                      borderColor: 'primary.main'
                    }} 
                  />
                  <Box sx={{ ml: 2, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {feedback.username}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                      Created: {new Date(feedback.time_stamp).toLocaleString()}
                    </Typography>
                    {isDeleted && feedback.deletion_time && (
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                        <DeleteIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                        Deleted: {new Date(feedback.deletion_time).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  {!isDeleted && isAdmin && variant !== 'user' && (
                    <Tooltip title="Delete feedback">
                      <IconButton 
                        onClick={() => handleDelete(feedback.id, true)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Typography 
                  variant="body1" 
                  sx={{ 
                    p: 2.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.secondary' : 'action.hover',
                    borderRadius: 1,
                    wordBreak: 'break-word',
                    color: 'text.primary',
                    boxShadow: 'inset 0 0 0 1px rgba(144, 202, 249, 0.08)',
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}
                >
                  {feedback.comment}
                </Typography>

                {!isDeleted && (
                  <ReplyForm 
                    feedbackId={feedback.id}
                    onReplySuccess={() => handleReplySuccess(feedback.id)}
                  />
                )}

                {hasReplies && (
                  <Box 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandThread(feedback.id);
                    }}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      mt: 2,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: 'background.secondary',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                      }
                    }}
                  >
                    <CommentIcon sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="button">
                      {isExpanded ? 'Hide' : 'Show'} {feedback.replies_count} {feedback.replies_count === 1 ? 'reply' : 'replies'}
                    </Typography>
                    <ExpandMoreIcon 
                      sx={{ 
                        transition: 'transform 0.2s ease-in-out',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }} 
                    />
                  </Box>
                )}

                <Collapse in={isExpanded} timeout={300}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ 
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
                    pt: 0,
                    maxHeight: '500px',
                    overflowY: 'auto',
                    borderRadius: 1,
                    mt: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: (theme) => theme.palette.primary.main,
                      borderRadius: '4px',
                      opacity: 0.5,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: (theme) => theme.palette.primary.dark,
                    }
                  }}>
                    {loadingThreads[feedback.id] ? (
                      <>
                        <RenderSkeleton />
                        <RenderSkeleton depth={1} />
                        <RenderSkeleton depth={1} />
                      </>
                    ) : (
                      threadResponses[feedback.id]?.replies?.map((reply) => (
                        <RenderReply 
                          key={reply.id} 
                          reply={reply}
                          onReplySuccess={() => handleReplySuccess(feedback.id)}
                          users={users}
                        />
                      ))
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Paper>
        );
      })}

      <Box sx={{ 
        position: 'fixed',
        bottom: 0,
        left: 200,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        py: 2,
        bgcolor: 'background.default',
        borderTop: 1,
        borderColor: 'divider',
        zIndex: 1000,
        boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <Pagination 
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
          sx={{
            '& .MuiPaginationItem-root': {
              color: 'text.primary',
            },
            '& .Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default FeedbackList;
